/**
 * 主题切换组件 - System / Light / Dark / Geek
 */
import React, { useMemo, useState } from 'react'
import { Dropdown } from 'antd'
import {
  CheckOutlined,
  SkinOutlined,
  DesktopOutlined,
  SunOutlined,
  MoonOutlined,
  CodeOutlined,
  RobotOutlined,
  BgColorsOutlined,
  ExperimentOutlined,
  ApiOutlined,
  CloudOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useTheme, type ThemeMode } from '@/hooks'

interface ThemeSwitcherProps {
  /** 自定义类名 */
  className?: string
  /** 渲染模式 */
  variant?: 'default' | 'toolbar'
}

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ className, variant = 'default' }) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('common')
  const { themeMode, setThemeMode } = useTheme()
  const overlayStyle = { zIndex: 1400 }

  const items = [
    {
      key: 'system',
      label: t('theme.system'),
      icon: <DesktopOutlined />,
      preview: 'linear-gradient(145deg, #f3f4f6 0%, #e5e7eb 62%, #d1d5db 100%)',
      labelColor: '#334155',
    },
    {
      key: 'light',
      label: t('theme.light'),
      icon: <SunOutlined style={{ color: '#d97706' }} />,
      preview: 'linear-gradient(145deg, #fff7cc 0%, #fde68a 62%, #f59e0b 100%)',
      labelColor: '#5b3b00',
    },
    {
      key: 'dark',
      label: t('theme.dark'),
      icon: <MoonOutlined />,
      preview: 'linear-gradient(145deg, #374151 0%, #1f2937 58%, #111827 100%)',
      labelColor: '#ffffff',
    },
    {
      key: 'geek',
      label: t('theme.geek'),
      icon: <CodeOutlined style={{ color: '#e2e8f0' }} />,
      preview: 'linear-gradient(145deg, #1f2a44 0%, #172554 58%, #0f172a 100%)',
      labelColor: '#ffffff',
    },
    {
      key: 'hlight',
      label: t('theme.heroLight'),
      icon: <BgColorsOutlined style={{ color: '#7c3aed' }} />,
      preview: 'linear-gradient(145deg, #f5ecff 0%, #ddd6fe 62%, #a78bfa 100%)',
      labelColor: '#4c1d95',
    },
    {
      key: 'hdark',
      label: t('theme.heroDark'),
      icon: <ExperimentOutlined style={{ color: '#a78bfa' }} />,
      preview: 'linear-gradient(145deg, #3b245f 0%, #2e1065 58%, #1e1b4b 100%)',
      labelColor: '#ffffff',
    },
    {
      key: 'tech',
      label: t('theme.tech'),
      icon: <ApiOutlined style={{ color: '#06b6d4' }} />,
      preview: 'linear-gradient(145deg, #174e63 0%, #155e75 58%, #0f172a 100%)',
      labelColor: '#ecfeff',
    },
    {
      key: 'blue',
      label: t('theme.blue'),
      icon: <CloudOutlined style={{ color: '#dbeafe' }} />,
      preview: 'linear-gradient(145deg, #5ca9ff 0%, #2563eb 62%, #1d4ed8 100%)',
      labelColor: '#eff6ff',
    },
    {
      key: 'cyber',
      label: t('theme.cyber'),
      icon: <RobotOutlined style={{ color: '#67e8f9' }} />,
      preview: 'linear-gradient(145deg, #103249 0%, #0f766e 58%, #082f49 100%)',
      labelColor: '#dafeff',
    },
  ]
  const activeLabel = items.find((item) => item.key === themeMode)?.label ?? t('theme.mode')
  const themePanel = useMemo(() => (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gridTemplateRows: 'repeat(3, minmax(0, 1fr))',
        gap: 12,
        padding: 16,
        borderRadius: 20,
        border: '1px solid color-mix(in srgb, var(--color-border) 82%, transparent)',
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-card) 96%, #ffffff 4%), color-mix(in srgb, var(--color-card) 88%, var(--color-bg-subtle) 12%))',
        boxShadow: 'inset 0 1px 0 color-mix(in srgb, #ffffff 70%, transparent)',
        boxSizing: 'border-box',
      }}
    >
      {items.map((item) => {
        const selected = item.key === themeMode

        return (
          <button
            key={item.key}
            type="button"
            aria-pressed={selected}
            onClick={() => {
              setThemeMode(item.key as ThemeMode)
              setOpen(false)
            }}
            style={{
              position: 'relative',
              minWidth: 0,
              minHeight: 0,
              height: '100%',
              padding: '10px 12px',
              boxSizing: 'border-box',
              borderRadius: 14,
              border: selected
                ? '3px solid var(--color-primary)'
                : '1px solid color-mix(in srgb, var(--color-border) 84%, transparent)',
              background: item.preview,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              overflow: 'hidden',
              boxShadow: selected
                ? '0 12px 24px color-mix(in srgb, var(--color-primary) 18%, transparent)'
                : '0 8px 18px color-mix(in srgb, #0f172a 10%, transparent)',
              transform: selected ? 'translateY(-1px)' : 'translateY(0)',
              transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.02) 42%, rgba(15,23,42,0.16) 100%)',
              }}
            />
            {selected && (
              <span
                style={{
                  position: 'absolute',
                  top: 7,
                  right: 7,
                  zIndex: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                  color: '#ffffff',
                  textShadow: '0 2px 6px rgba(15, 23, 42, 0.35)',
                }}
              >
                <CheckOutlined
                  style={{
                    display: 'block',
                    fontSize: 18,
                    fontWeight: 800,
                    lineHeight: 1,
                    verticalAlign: '0',
                    position: 'relative',
                    top: '-1px',
                  }}
                />
              </span>
            )}
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                width: '100%',
                height: '100%',
                minHeight: 0,
                paddingTop: selected ? 4 : 0,
                paddingRight: selected ? 20 : 0,
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto',
                  width: 28,
                  minHeight: 24,
                  fontSize: 22,
                  lineHeight: 1,
                  color: item.labelColor,
                  textShadow: '0 1px 2px rgba(15, 23, 42, 0.2)',
                }}
              >
                {item.icon}
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  letterSpacing: '0.01em',
                  color: item.labelColor,
                  textAlign: 'left',
                  textShadow: item.labelColor === '#334155'
                    ? 'none'
                    : '0 1px 2px rgba(15, 23, 42, 0.42)',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {item.label}
              </div>
            </div>
          </button>
        )
      })}
    </div>
  ), [items, setThemeMode, themeMode, t])
  const dropdownContent = useMemo(() => (
    <div
      style={{
        width: 520,
        maxWidth: 'min(520px, calc(100vw - 32px))',
        height: 260,
        padding: 22,
        borderRadius: 28,
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-card) 98%, #ffffff 2%), color-mix(in srgb, var(--color-card) 94%, var(--color-bg-subtle) 6%))',
        border: '1px solid color-mix(in srgb, var(--color-border) 88%, transparent)',
        boxShadow: '0 28px 60px color-mix(in srgb, #0f172a 18%, transparent)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)' }}>
          {t('theme.mode')}
        </span>
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)' }}>
          {activeLabel}
        </span>
      </div>
      {themePanel}
    </div>
  ), [activeLabel, t, themePanel])

  if (variant === 'toolbar') {
    return (
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        placement="bottomRight"
        trigger={['click']}
        overlayStyle={overlayStyle}
        dropdownRender={() => dropdownContent}
      >
        <span
          className={className}
          data-open={open ? 'true' : 'false'}
          data-theme-mode={themeMode}
          aria-label={t('theme.mode')}
          style={className ? undefined : {
            fontSize: 16,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: open ? 'var(--color-primary)' : 'var(--color-text-secondary)',
          }}
        >
          <SkinOutlined style={{ fontSize: 18 }} />
        </span>
      </Dropdown>
    )
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      overlayStyle={overlayStyle}
      menu={{
        items,
        selectable: true,
        selectedKeys: [themeMode],
        onClick: ({ key }) => {
          setThemeMode(key as ThemeMode)
          setOpen(false)
        },
      }}
      placement="bottomRight"
      trigger={['click']}
    >
      <span
        className={className}
        data-open={open ? 'true' : 'false'}
        data-theme-mode={themeMode}
        aria-label={t('theme.mode')}
        style={className ? undefined : {
          fontSize: 16,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          color: 'var(--color-text-secondary)',
        }}
      >
        <SkinOutlined style={{ fontSize: 14 }} />
        <span>{activeLabel}</span>
      </span>
    </Dropdown>
  )
}

export default ThemeSwitcher
